/**
 * Optimistic HELM item actions (A7Y-3870).
 *
 * Applies the acted-on item's decision to the cached `['helm-overview']`
 * query immediately (mirroring what `applyHelmActions` does server-side on
 * the next run), fires the mutation, and offers one Undo — mirroring the
 * ticket's "applied to the view immediately with undo, and merged by the
 * next run (user action wins on conflict)". The polling `HelmPage` effect
 * reconciles with the server on its next fetch regardless, so this is a
 * perceived-latency win, not the source of truth.
 *
 * Every mutation here can fail (a rejected create, a
 * failed undo), and a failed `createHelmAction` must not leave its optimistic
 * patch standing. `act`/`undoActionById` snapshot the query cache before
 * mutating and restore it verbatim on a rejected call, then rethrow so the
 * caller (`HelmPage`) decides how to surface the failure — this hook owns
 * cache consistency, not UI error text.
 *
 * What a decided item hides FROM is `overview.suppressedKeys`
 * — the server's own `isHelmItemSuppressed` verdict — not a client-side
 * predicate over the decision's kind. So the optimistic decision patch also
 * adds the acted-on key to the cached `suppressedKeys`, and a rejected call
 * rolls both back together via the same whole-overview snapshot/restore.
 */
import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  HelmActionInput,
  HelmActionKind,
  HelmCapture,
  HelmCommitment,
  HelmDecision,
  HelmDeployedItem,
  HelmFacts,
  HelmOverviewResponse,
} from '@a7y/platform-health/helm';
import { api } from '@/lib/api';

export const HELM_OVERVIEW_QUERY_KEY = ['helm-overview'] as const;

interface Keyed {
  key: string;
  decision: HelmDecision | null;
}

function patchByKey<T extends Keyed>(items: T[], key: string, decision: HelmDecision | null): T[] {
  return items.map((item) => (item.key === key ? { ...item, decision } : item));
}

function patchFactsDecision(
  facts: HelmFacts,
  key: string,
  decision: HelmDecision | null
): HelmFacts {
  return {
    ...facts,
    meetings: patchByKey(facts.meetings, key, decision),
    inbox: patchByKey(facts.inbox, key, decision),
    sent: patchByKey(facts.sent, key, decision),
    linkedin_mail: patchByKey(facts.linkedin_mail, key, decision),
    tickets: patchByKey(facts.tickets, key, decision),
    deployed: patchByKey<HelmDeployedItem>(facts.deployed, key, decision),
    commitments: patchByKey<HelmCommitment>(facts.commitments, key, decision),
    git_since: patchByKey(facts.git_since, key, decision),
    drive_notes: patchByKey(facts.drive_notes, key, decision),
    new_senders: patchByKey(facts.new_senders, key, decision),
    captures: patchByKey(facts.captures, key, decision),
    proposals: patchByKey(facts.proposals, key, decision),
    judgment_queue: {
      ...facts.judgment_queue,
      items: patchByKey(facts.judgment_queue.items, key, decision),
    },
  };
}

function appendOptimisticCapture(facts: HelmFacts, capture: HelmCapture): HelmFacts {
  return { ...facts, captures: [...facts.captures, capture] };
}

function withSuppressedKey(suppressedKeys: string[], key: string): string[] {
  return suppressedKeys.includes(key) ? suppressedKeys : [...suppressedKeys, key];
}

export function useHelmActions() {
  const queryClient = useQueryClient();
  const [lastActionId, setLastActionId] = useState<string | null>(null);

  const snapshot = useCallback(
    () => queryClient.getQueryData<HelmOverviewResponse>(HELM_OVERVIEW_QUERY_KEY),
    [queryClient]
  );

  const restore = useCallback(
    (prev: HelmOverviewResponse | undefined) => {
      if (prev) queryClient.setQueryData<HelmOverviewResponse>(HELM_OVERVIEW_QUERY_KEY, prev);
    },
    [queryClient]
  );

  const applyOptimisticDecision = useCallback(
    (itemKey: string, decision: HelmDecision | null) => {
      queryClient.setQueryData<HelmOverviewResponse>(HELM_OVERVIEW_QUERY_KEY, (prev) => {
        if (!prev?.current) return prev;
        return {
          ...prev,
          current: {
            ...prev.current,
            facts: patchFactsDecision(prev.current.facts, itemKey, decision),
          },
          // The server recomputes `suppressedKeys` from `isHelmItemSuppressed`
          // on every fetch; this optimistic addition is only to hide the item
          // immediately, and is replaced wholesale by the next real fetch.
          suppressedKeys: withSuppressedKey(prev.suppressedKeys, itemKey),
        };
      });
    },
    [queryClient]
  );

  const applyOptimisticCapture = useCallback(
    (capture: HelmCapture) => {
      queryClient.setQueryData<HelmOverviewResponse>(HELM_OVERVIEW_QUERY_KEY, (prev) => {
        if (!prev?.current) return prev;
        return {
          ...prev,
          current: { ...prev.current, facts: appendOptimisticCapture(prev.current.facts, capture) },
        };
      });
    },
    [queryClient]
  );

  /** `undoable: false` (A7Y-5210) for an overlay that follows a record write: undoing the overlay could not undo the record, so no Undo is offered. */
  const act = useCallback(
    async (input: HelmActionInput, options: { undoable?: boolean } = {}) => {
      const before = snapshot();
      if (input.kind === 'capture') {
        // Never targets an existing item — appends a new entry to
        // `facts.captures[]` so a submitted note shows immediately
        // rather than waiting for the next poll to produce it.
        const optimisticCapture: HelmCapture = {
          key: `capture:optimistic-${Date.now()}`,
          evidence: {
            source: 'page',
            observedAt: new Date().toISOString(),
            attribution: null,
            ref: { kind: 'page_capture', id: 'optimistic', url: null },
          },
          interpretation: null,
          decision: null,
          critical: [],
          note: input.note ?? '',
          capturedAt: new Date().toISOString(),
          actor: { userId: '', email: '', name: '' },
        };
        applyOptimisticCapture(optimisticCapture);
      } else if (input.itemKey) {
        const optimisticDecision: HelmDecision = {
          actionId: `optimistic:${input.itemKey}:${Date.now()}`,
          kind: input.kind as Exclude<HelmActionKind, 'capture'>,
          actor: { userId: '', email: '', name: '' },
          at: new Date().toISOString(),
          deferUntil: input.deferUntil ?? null,
          waitingOn: input.waitingOn ?? null,
          waitingSide: input.waitingSide ?? null,
          // F21: recheckOn is a waiting_on field — it must come from the
          // caller's own recheckOn, never from deferUntil (a different
          // action kind's own follow-up date).
          recheckOn: input.recheckOn ?? null,
          note: input.note ?? null,
          // The real proposal-effect fields ({key, kind, effect}) are only
          // known once the server applies the accepted decision inside
          // `applyHelmActions` (D9) — this optimistic patch shows the
          // approve/reject decision immediately without guessing them.
          proposal: null,
        };
        applyOptimisticDecision(input.itemKey, optimisticDecision);
      }

      try {
        const { action } = await api.createHelmAction(input);
        setLastActionId(options.undoable === false ? null : action.actionId);
        await queryClient.invalidateQueries({ queryKey: HELM_OVERVIEW_QUERY_KEY });
      } catch (err) {
        restore(before);
        throw err;
      }
    },
    [applyOptimisticCapture, applyOptimisticDecision, queryClient, restore, snapshot]
  );

  /** Undo any action by id — used both by the "last action" banner and by a
   * per-capture Undo button, which targets an action that may no
   * longer be the most recent one. Undo applies no optimistic patch of its
   * own (the item's decision stays until the server confirms the reversal),
   * so a rejected call has nothing to roll back — it only needs to reach the
   * caller so the failure can be surfaced. */
  const undoActionById = useCallback(
    async (actionId: string) => {
      await api.undoHelmAction(actionId);
      if (lastActionId === actionId) setLastActionId(null);
      await queryClient.invalidateQueries({ queryKey: HELM_OVERVIEW_QUERY_KEY });
    },
    [lastActionId, queryClient]
  );

  const undo = useCallback(async () => {
    if (!lastActionId) return;
    await undoActionById(lastActionId);
  }, [lastActionId, undoActionById]);

  return { act, undo, undoActionById, canUndo: lastActionId != null };
}
